/**
 * Pusher/Laravel Echo Composable (Singleton)
 *
 * Provides real-time WebSocket connection using Laravel Echo with Pusher.
 * Used for real-time notifications, messages, and other live updates.
 *
 * This is a SINGLETON - the Echo connection is created once and shared across
 * all components. Connection lifecycle is managed by auth state, not component lifecycle.
 */

import { reactive, computed, readonly, watch } from "vue";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { useAuth } from "./useAuth";
import { getApiBaseUrl } from "./useSdk";

// Type for channel authorization response
interface ChannelAuthData {
  auth: string;
  channel_data?: string;
}

// Token storage key (same as authService)
const TOKEN_KEY = "auth_token";

// Extend window to include Pusher and Echo
declare global {
  interface Window {
    Pusher: typeof Pusher;
    Echo: Echo<"pusher">;
  }
}

export interface PusherConfig {
  key: string;
  cluster?: string;
  wsHost?: string;
  wsPort?: number;
  wssPort?: number;
  forceTLS?: boolean;
  encrypted?: boolean;
  enabledTransports?: string[];
  authEndpoint?: string;
}

export interface ConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  reconnectAttempts: number;
  lastConnected: Date | null;
  socketId: string | null;
}

export interface ChatMessageEvent {
  conversation_id: string;
  message: {
    id?: number;
    ulid: string;
    body: string;
    createdAt: string;
    username: string;
    user: {
      userId: string;
      avatar?: string | null;
      name?: string | null;
    };
  };
}

export interface NotificationEvent {
  id: string;
  type: string;
  data: Record<string, unknown>;
  createdAt: string;
  readAt?: string;
}

export interface ChatUnreadCountEvent {
  conversationUlid: string;
  unreadCount: number;
  totalUnreadCount: number;
}

export interface PostImageFinishedEvent {
  ulid: string;
  message: string;
}

export interface PostProcessingCompleteEvent {
  ulid: string;
  type: string;
}

export interface PlaylistCoverProcessedEvent {
  playlistUlid: string;
  coverImage: string;
}

/**
 * Get current auth token from storage
 */
function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

// ============================================================================
// SINGLETON STATE - shared across all usePusher() calls
// ============================================================================

// Connection state (reactive, shared)
const connection = reactive<ConnectionState>({
  isConnected: false,
  isConnecting: false,
  reconnectAttempts: 0,
  lastConnected: null,
  socketId: null,
});

// Private singleton state
let echo: Echo<"pusher"> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const subscriptions = new Map<string, unknown>();

// Track if global event listeners have been set up
let globalListenersInitialized = false;

// Track if auth watcher has been set up
let authWatcherInitialized = false;

// Computed connection status
const connectionStatus = computed(() => {
  if (connection.isConnecting) return "connecting";
  if (connection.isConnected) return "connected";
  return "disconnected";
});

/**
 * Get Pusher configuration from environment
 */
function getConfig(): PusherConfig {
  const apiBaseUrl = getApiBaseUrl();

  // Log raw env values for debugging (only once)
  if (!echo) {
    console.log("[Pusher] Raw env values:", {
      VITE_PUSHER_APP_KEY: import.meta.env.VITE_PUSHER_APP_KEY,
      VITE_PUSHER_APP_CLUSTER: import.meta.env.VITE_PUSHER_APP_CLUSTER,
      VITE_ECHO_HOST: import.meta.env.VITE_ECHO_HOST,
      VITE_ECHO_PORT: import.meta.env.VITE_ECHO_PORT,
      VITE_API_HOST: import.meta.env.VITE_API_HOST,
      resolvedApiBaseUrl: apiBaseUrl,
    });
  }

  const config = {
    key:
      import.meta.env.VITE_PUSHER_APP_KEY || "b41884ecb6daa42e5743bdeadbeef",
    cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER || "",
    wsHost: import.meta.env.VITE_ECHO_HOST || "s.closedcircuit.io",
    wsPort: parseInt(import.meta.env.VITE_ECHO_PORT || "443", 10),
    wssPort: parseInt(import.meta.env.VITE_ECHO_PORT || "443", 10),
    forceTLS: true,
    encrypted: true,
    enabledTransports: ["ws", "wss"],
    authEndpoint: `${apiBaseUrl}/broadcasting/auth`,
  };

  if (!echo) {
    console.log("[Pusher] Resolved config:", config);
  }

  return config;
}

/**
 * Initialize Echo connection (singleton)
 */
function connect(): void {
  if (echo || connection.isConnecting) {
    return;
  }

  const currentToken = getToken();
  if (!currentToken) {
    console.log("[Pusher] No token available, skipping connection");
    return;
  }

  connection.isConnecting = true;
  const config = getConfig();

  try {
    // Make Pusher available globally
    window.Pusher = Pusher;

    console.log("[Pusher] Creating Echo instance with config:", {
      key: config.key,
      wsHost: config.wsHost,
      wsPort: config.wsPort,
      authEndpoint: config.authEndpoint,
    });

    // Create Echo instance with custom authorizer
    echo = new Echo({
      broadcaster: "pusher",
      key: config.key,
      cluster: config.cluster,
      wsHost: config.wsHost,
      wsPort: config.wsPort,
      wssPort: config.wssPort,
      forceTLS: config.forceTLS,
      encrypted: config.encrypted,
      disableStats: true,
      enabledTransports: config.enabledTransports as ("ws" | "wss")[],
      authorizer: (channel: { name: string }) => {
        return {
          authorize: (
            socketId: string,
            callback: (
              error: Error | null,
              data: ChannelAuthData | null,
            ) => void,
          ) => {
            const authToken = getToken();
            fetch(config.authEndpoint!, {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                Authorization: `Bearer ${authToken}`,
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
              },
              body: new URLSearchParams({
                socket_id: socketId,
                channel_name: channel.name,
              }),
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`Authorization failed: ${response.status}`);
                }
                return response.json();
              })
              .then((data: ChannelAuthData) => {
                callback(null, data);
              })
              .catch((error) => {
                console.error("[Pusher] Auth error:", error);
                callback(error as Error, null);
              });
          },
        };
      },
    });

    window.Echo = echo;

    // Setup connection handlers
    const pusher = (echo as unknown as { connector: { pusher: Pusher } })
      .connector.pusher;

    pusher.connection.bind("connected", () => {
      console.log("[Pusher] Connected");
      connection.isConnected = true;
      connection.isConnecting = false;
      connection.reconnectAttempts = 0;
      connection.lastConnected = new Date();
      connection.socketId = pusher.connection.socket_id;
    });

    pusher.connection.bind("connecting", () => {
      connection.isConnecting = true;
      connection.isConnected = false;
    });

    pusher.connection.bind("disconnected", () => {
      console.log("[Pusher] Disconnected");
      connection.isConnected = false;
      connection.isConnecting = false;
      connection.socketId = null;
      scheduleReconnect();
    });

    pusher.connection.bind("failed", () => {
      console.error("[Pusher] Connection failed");
      connection.isConnected = false;
      connection.isConnecting = false;
      scheduleReconnect();
    });

    pusher.connection.bind("error", (error: unknown) => {
      console.error("[Pusher] Error:", error);
    });
  } catch (error) {
    console.error("[Pusher] Setup failed:", error);
    connection.isConnecting = false;
    scheduleReconnect();
  }
}

/**
 * Disconnect from WebSocket (only called on logout)
 */
function disconnect(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  if (echo) {
    // Unsubscribe from all channels
    subscriptions.forEach((_, channel) => {
      try {
        echo?.leave(channel);
      } catch (e) {
        console.warn("[Pusher] Error leaving channel:", channel, e);
      }
    });
    subscriptions.clear();

    echo.disconnect();
    echo = null;
    window.Echo = undefined as unknown as Echo<"pusher">;
  }

  connection.isConnected = false;
  connection.isConnecting = false;
  connection.socketId = null;
}

/**
 * Schedule reconnection with exponential backoff
 */
function scheduleReconnect(): void {
  if (connection.reconnectAttempts >= 10) {
    console.error("[Pusher] Max reconnect attempts reached");
    return;
  }

  // Check if user is still authenticated before scheduling
  const token = getToken();
  if (!token) {
    console.log("[Pusher] No token, skipping reconnect");
    return;
  }

  connection.reconnectAttempts++;
  const delay =
    Math.min(1000 * Math.pow(2, connection.reconnectAttempts - 1), 30000) +
    Math.random() * 1000;

  console.log(`[Pusher] Reconnecting in ${Math.round(delay)}ms...`);

  reconnectTimer = setTimeout(() => {
    const currentToken = getToken();
    if (!connection.isConnected && currentToken) {
      connect();
    }
  }, delay);
}

/**
 * Set up global event listeners (visibility, online/offline)
 * Only runs once per app lifecycle
 */
function setupGlobalListeners(): void {
  if (globalListenersInitialized || typeof window === "undefined") {
    return;
  }

  globalListenersInitialized = true;

  // Handle visibility changes
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && getToken() && !connection.isConnected) {
      connect();
    }
  });

  // Handle online/offline
  window.addEventListener("online", () => {
    if (getToken() && !connection.isConnected) {
      connect();
    }
  });

  window.addEventListener("offline", () => {
    // Don't fully disconnect on offline - let Pusher handle reconnection
    // Just update state
    connection.isConnected = false;
  });
}

// ============================================================================
// COMPOSABLE FUNCTION
// ============================================================================

/**
 * Pusher/Echo composable for real-time WebSocket connections (Singleton)
 *
 * The connection is shared across all components. Calling usePusher() multiple
 * times returns the same connection instance. The connection is only disconnected
 * when the user logs out, not when components unmount.
 */
export function usePusher() {
  const { isAuthenticated, user } = useAuth();

  // Set up global event listeners (only once)
  setupGlobalListeners();

  // Set up auth watcher (only once)
  if (!authWatcherInitialized) {
    authWatcherInitialized = true;

    watch(
      isAuthenticated,
      (newValue, oldValue) => {
        if (newValue && !oldValue) {
          // User logged in
          connect();
        } else if (!newValue && oldValue) {
          // User logged out
          disconnect();
        }
      },
      { immediate: false },
    );

    // Auto-connect if already authenticated
    if (isAuthenticated.value && getToken()) {
      connect();
    }
  }

  /**
   * Subscribe to a private channel
   * If already subscribed to the channel, adds new event listeners to existing subscription
   */
  function subscribePrivate<T>(
    channel: string,
    events: Record<string, (data: T) => void>,
  ): void {
    if (!isAuthenticated.value) {
      console.warn("[Pusher] Not authenticated");
      return;
    }

    // Auto-connect if not initialized
    if (!echo) {
      console.log("[Pusher] Auto-connecting before subscribing to:", channel);
      connect();
    }

    // If still not connected after connect attempt, skip
    if (!echo) {
      console.warn("[Pusher] Echo failed to initialize");
      return;
    }

    try {
      // Check if we already have a subscription to this channel
      const existingSubscription = subscriptions.get(channel) as
        | { listen: (event: string, handler: (data: T) => void) => void }
        | undefined;

      if (existingSubscription) {
        // Add new event listeners to existing subscription
        Object.entries(events).forEach(([event, handler]) => {
          existingSubscription.listen(event, handler);
        });
      } else {
        // Create new subscription
        const subscription = echo.private(channel);

        Object.entries(events).forEach(([event, handler]) => {
          subscription.listen(event, handler);
        });

        subscriptions.set(channel, subscription);
        console.log("[Pusher] Subscribed to private channel:", channel);
      }
    } catch (error) {
      console.error("[Pusher] Failed to subscribe to channel:", channel, error);
    }
  }

  /**
   * Subscribe to a public channel
   */
  function subscribe<T>(
    channel: string,
    events: Record<string, (data: T) => void>,
  ): void {
    if (!echo) {
      console.warn("[Pusher] Echo not initialized");
      return;
    }

    try {
      const subscription = echo.channel(channel);

      Object.entries(events).forEach(([event, handler]) => {
        subscription.listen(event, handler);
      });

      subscriptions.set(channel, subscription);
      console.log("[Pusher] Subscribed to channel:", channel);
    } catch (error) {
      console.error("[Pusher] Failed to subscribe to channel:", channel, error);
    }
  }

  /**
   * Unsubscribe from a channel
   */
  function unsubscribe(channel: string): void {
    if (subscriptions.has(channel)) {
      try {
        echo?.leave(channel);
        subscriptions.delete(channel);
        console.log("[Pusher] Unsubscribed from channel:", channel);
      } catch (error) {
        console.error("[Pusher] Failed to unsubscribe:", channel, error);
      }
    }
  }

  /**
   * Whisper an event to a private channel (client-only; does not hit backend).
   * Requires an existing subscription to the channel.
   */
  function whisper(channel: string, event: string, payload: unknown): void {
    const subscription = subscriptions.get(channel) as
      | { whisper?: (event: string, payload: unknown) => void }
      | undefined;
    if (!subscription?.whisper) {
      console.warn(
        "[Pusher] Cannot whisper - not subscribed to channel:",
        channel,
      );
      return;
    }
    try {
      subscription.whisper(event, payload);
    } catch (err) {
      console.warn("[Pusher] Whisper failed:", err);
    }
  }

  /**
   * Subscribe to chat messages for a conversation
   */
  function subscribeToChatMessages(
    conversationId: string | number,
    handler: (data: ChatMessageEvent) => void,
  ): void {
    subscribePrivate(`chat.${conversationId}`, {
      ".message.sent": handler,
      "message.sent": handler,
    });
  }

  /**
   * Subscribe to user notifications
   */
  function subscribeToNotifications(
    userUlid: string,
    handler: (data: NotificationEvent) => void,
  ): void {
    subscribePrivate(`notifications.${userUlid}`, {
      NotificationCount: handler,
      notification: handler,
    });
  }

  /**
   * Subscribe to post image finished events (for background post creation)
   */
  function subscribeToPostImageFinished(
    userUlid: string,
    handler: (data: PostImageFinishedEvent) => void,
  ): void {
    console.log(
      "[Pusher] Subscribing to post image finished events for user:",
      userUlid,
    );
    subscribePrivate(`notifications.${userUlid}`, {
      ".PostImageFinished": handler,
      PostImageFinished: handler,
      "App\\Events\\Posts\\PostImageFinished": handler,
    });
  }

  /**
   * Subscribe to post processing complete events (for background post creation)
   * This event fires when the entire post processing pipeline is complete
   * Broadcasts on the user-{ulid} channel (same as chat unread updates)
   */
  function subscribeToPostProcessingComplete(
    userUlid: string,
    handler: (data: PostProcessingCompleteEvent) => void,
  ): void {
    console.log(
      "[Pusher] Subscribing to post processing complete events for user:",
      userUlid,
    );
    subscribePrivate(`user-${userUlid}`, {
      ".PostProcessingComplete": handler,
      PostProcessingComplete: handler,
      "App\\Events\\Posts\\PostProcessingComplete": handler,
    });
  }

  /**
   * Subscribe to post channel for real-time comment updates
   * Listens for comment.added events when new comments are posted
   */
  function subscribeToPostComments(
    postUlid: string,
    handler: (data: unknown) => void,
  ): void {
    console.log(
      "[Pusher] Subscribing to post comments channel:",
      `post.${postUlid}`,
    );
    subscribePrivate(`post.${postUlid}`, {
      "comment.added": handler,
      ".comment.added": handler,
      "App\\Events\\Posts\\CommentAdded": handler,
    });
  }

  /**
   * Subscribe to typing indicators
   */
  function subscribeToTyping(
    conversationId: string | number,
    handlers: {
      typing?: (data: { user_id: string | number; is_typing: boolean }) => void;
      stoppedTyping?: (data: { user_id: string | number }) => void;
    },
  ): void {
    const events: Record<string, (data: unknown) => void> = {};

    if (handlers.typing) {
      events["UserTyping"] = handlers.typing as (data: unknown) => void;
      events[".typing"] = handlers.typing as (data: unknown) => void;
    }
    if (handlers.stoppedTyping) {
      events["UserStoppedTyping"] = handlers.stoppedTyping as (
        data: unknown,
      ) => void;
      events[".stopped-typing"] = handlers.stoppedTyping as (
        data: unknown,
      ) => void;
    }

    subscribePrivate(`chat.${conversationId}`, events);

    // Also subscribe to Echo whispers (used by older clients / optional realtime typing UX).
    // Echo emits whispers via listenForWhisper('typing', cb).
    const channelName = `chat.${conversationId}`;
    const subscription = subscriptions.get(channelName) as
      | {
          listenForWhisper?: (
            event: string,
            cb: (data: unknown) => void,
          ) => void;
        }
      | undefined;
    if (subscription?.listenForWhisper) {
      if (handlers.typing) {
        subscription.listenForWhisper(
          "typing",
          handlers.typing as (d: unknown) => void,
        );
      }
      if (handlers.stoppedTyping) {
        subscription.listenForWhisper(
          "stopped-typing",
          handlers.stoppedTyping as (d: unknown) => void,
        );
      }
    }
  }

  /**
   * Send a typing indicator to other clients in the conversation using Echo whispers.
   * Requires you already subscribed to `chat.{conversationId}`.
   */
  function sendTypingIndicator(
    conversationId: string | number,
    isTyping: boolean,
  ): void {
    const channelName = `chat.${conversationId}`;
    const userId =
      (user.value as Record<string, unknown>)?.id ?? user.value?.ulid;
    if (!userId) return;
    whisper(channelName, "typing", { user_id: userId, is_typing: isTyping });
  }

  /**
   * Subscribe to user's private channel for chat unread count updates.
   * This allows receiving unread count updates without subscribing to each conversation.
   */
  function subscribeToUserChannel(
    userUlid: string,
    handler: (data: ChatUnreadCountEvent) => void,
  ): void {
    console.log("[Pusher] Subscribing to user channel:", `user-${userUlid}`);
    subscribePrivate(`user-${userUlid}`, {
      ".chat.unread.updated": handler,
      "chat.unread.updated": handler,
    });
  }

  /**
   * Unsubscribe from user's private channel
   */
  function unsubscribeFromUserChannel(userUlid: string): void {
    unsubscribe(`user-${userUlid}`);
  }

  /**
   * Subscribe to playlist cover image processed events
   * Fires when async cover image processing completes
   */
  function subscribeToPlaylistCoverProcessed(
    userUlid: string,
    handler: (data: PlaylistCoverProcessedEvent) => void,
  ): void {
    console.log(
      "[Pusher] Subscribing to playlist cover processed events for user:",
      userUlid,
    );
    subscribePrivate(`user-${userUlid}`, {
      ".playlist.cover.processed": handler,
      "playlist.cover.processed": handler,
    });
  }

  return {
    // State (readonly to prevent external mutation)
    connection: readonly(connection),
    connectionStatus,

    // Methods
    connect,
    disconnect,
    subscribe,
    subscribePrivate,
    unsubscribe,
    subscribeToChatMessages,
    subscribeToNotifications,
    subscribeToPostImageFinished,
    subscribeToPostProcessingComplete,
    subscribeToPostComments,
    subscribeToTyping,
    whisper,
    sendTypingIndicator,
    subscribeToUserChannel,
    unsubscribeFromUserChannel,
    subscribeToPlaylistCoverProcessed,
  };
}
